import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const contactSchema = z.object({
  company: z.string().trim().min(1, "Fältet är obligatoriskt").max(100),
  name: z.string().trim().min(1, "Fältet är obligatoriskt").max(100),
  email: z.string().trim().email("Ange en giltig e-postadress").max(255),
  role: z.string().trim().max(100).optional(),
  interest: z.string().min(1, "Välj ett alternativ"),
  message: z.string().trim().max(1000).optional(),
});

type ContactForm = z.infer<typeof contactSchema>;

export function CTASection() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const mountedAt = useRef(Date.now());

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      company: "",
      name: "",
      email: "",
      role: "",
      interest: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("contact_submissions").insert({
        type: "contact",
        company: data.company,
        name: data.name,
        email: data.email,
        role: data.role || null,
        interest: data.interest,
        message: data.message || null,
      });

      if (error) throw error;

      // Send email via edge function (CTA = demo type)
      await supabase.functions.invoke("send-contact-email", {
        body: {
          type: "demo",
          company: data.company,
          name: data.name,
          email: data.email,
          role: data.role || undefined,
          interest: data.interest,
          message: data.message || undefined,
          honeypot,
          _elapsed: Date.now() - mountedAt.current,
        },
      });

      toast.success(t("cta.form.success"));
      form.reset();
    } catch (err) {
      console.error("CTA form submission error:", err);
      toast.error(t("kontakt.form.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 lg:py-28 bg-gradient-to-br from-coral via-coral to-coral/90 text-coral-foreground">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Text Side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-32"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium font-heading tracking-tight">
              {t("cta.title")}
            </h2>
            <p className="mt-4 text-lg text-coral-foreground/80 font-body leading-relaxed max-w-md">
              {t("cta.subtitle")}
            </p>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card text-card-foreground rounded-2xl p-5 sm:p-8 shadow-2xl"
          >
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                {/* Honeypot field - hidden from real users */}
                <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("cta.form.company")}</FormLabel>
                        <FormControl>
                          <Input placeholder="Företag AB" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("cta.form.name")}</FormLabel>
                        <FormControl>
                          <Input placeholder="Anna Andersson" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("cta.form.email")}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="anna@foretag.se"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("cta.form.role")}</FormLabel>
                        <FormControl>
                          <Input placeholder="Logistikchef" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="interest"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("cta.form.interest")}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Välj ett alternativ" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="atlas">
                            {t("cta.form.interestOptions.atlas")}
                          </SelectItem>
                          <SelectItem value="valid">
                            {t("cta.form.interestOptions.valid")}
                          </SelectItem>
                          <SelectItem value="adresstwatt">
                            {t("cta.form.interestOptions.adresstwatt")}
                          </SelectItem>
                          <SelectItem value="general">
                            {t("cta.form.interestOptions.general")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("cta.form.message")}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Berätta gärna mer om ert behov..."
                          rows={4}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {t("cta.form.submit")}
                </Button>
              </form>
            </Form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
